import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Icon } from "@iconify/react";
import { leads } from "../../common/db";
import AuthContext from "../../contexts/AuthContext";
import {
  changeDate,
  updatingIndexedData,
  updateLeadApi,
  safeJSONParse,
} from "../../common/functions";
// iOS keyboard is now handled via CSS dvh units and viewport meta tag
import "./EditCommentModal.css";

const EditCommentModal = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [singleLead, setSingleLead] = useState({});
  const [commentText, setCommentText] = useState("");
  const [hasHistory, setHasHistory] = useState(false);
  const [disableSave, setDisableSave] = useState(true);
  const [loading, setLoading] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine);

  // Pull-down gesture state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const modalRef = useRef(null);

  const Context = useContext(AuthContext);
  const navigate = useNavigate();
  const parentContext = useOutletContext();

  const fetch_url_search = window.location.search;
  const urlParams = new URLSearchParams(fetch_url_search);
  const bookingId = urlParams.get("bookingId");
  const event_id = parentContext?.event_id;

  const localProfileName = localStorage.getItem("profileName");
  const profileName =
    localProfileName && localProfileName !== "undefined"
      ? safeJSONParse(localProfileName)
      : Context?.state?.user?.attributes?.name ||
        Context?.state?.user?.attributes?.email ||
        "reachus@konfhub.com";

  const today = new Date();
  const currentTime = today.getTime();

  useEffect(() => {
    const handleOnline = () => setNetworkStatus(true);
    const handleOffline = () => setNetworkStatus(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const fetchAttendeeDetails = async () => {
      const leadDetailsArr = await leads.where("id").equals(bookingId).toArray();
      if (leadDetailsArr[0]) {
        setSingleLead(leadDetailsArr[0]);
        const comments = leadDetailsArr[0]?.comment;
        if (comments && comments !== "undefined" && comments.length > 0) {
          const lastComment = safeJSONParse(comments[comments.length - 1]);
          setCommentText(lastComment?.comment || "");
          setHasHistory(true);
        }
        setDisableSave(true);
      }
    };
    fetchAttendeeDetails();
  }, [bookingId]);

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setCommentText(value);

    const comments = singleLead?.comment;
    if (comments && comments !== "undefined" && comments.length > 0) {
      const lastComment = safeJSONParse(comments[comments.length - 1]);
      setDisableSave(value === lastComment?.comment);
    } else {
      setDisableSave(!value);
    }
  };

  const handleSaveComment = async () => {
    if (!commentText.trim()) return;

    setLoading(true);

    const commentObject = {
      commentedBy: profileName,
      comment: commentText,
      commentedAt: currentTime,
      isOnline: networkStatus,
    };

    const fetchAttendeeDetailsFromDB = await leads
      .where("id")
      .equals(singleLead.bookingId)
      .toArray();

    let allComments = [];
    if (
      fetchAttendeeDetailsFromDB[0]?.comment &&
      fetchAttendeeDetailsFromDB[0].comment !== "undefined"
    ) {
      allComments = [
        ...fetchAttendeeDetailsFromDB[0].comment,
        JSON.stringify(commentObject),
      ];
    } else {
      allComments = [JSON.stringify(commentObject)];
    }

    if (networkStatus) {
      updatingIndexedData({
        key: singleLead.bookingId,
        network: true,
        comment: allComments,
      });
      try {
        updateLeadApi([
          {
            sponsorCode: singleLead.sponsorCode,
            bookingId: singleLead.bookingId,
            eventId: event_id,
            comment: JSON.stringify(commentObject),
          },
        ]);
      } catch (err) {
        console.log(err);
      }
    } else {
      updatingIndexedData({
        key: singleLead.bookingId,
        network: false,
        comment: allComments,
      });
    }

    setLoading(false);
    handleClose();
  };

  const handleClose = () => {
    if (isClosing) return; // Prevent double-trigger from rapid clicks
    setIsClosing(true);
    setIsVisible(false);
    navigate(-1);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Pull-down gesture handlers
  const handleTouchStart = useCallback((e) => {
    // Only enable drag from the drawer bar area (top 50px of modal)
    const touch = e.touches[0];
    const modalRect = modalRef.current?.getBoundingClientRect();
    if (modalRect && touch.clientY - modalRect.top < 50) {
      dragStartY.current = touch.clientY;
      setIsDragging(true);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragStartY.current;
    // Only allow dragging down (positive deltaY)
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    // Close modal if dragged more than 100px down
    if (dragY > 100) {
      handleClose();
    } else {
      // Snap back
      setDragY(0);
    }
  }, [isDragging, dragY]);

  // Get comment history
  const getCommentHistory = () => {
    const comments = singleLead?.comment;
    if (!comments || comments === "undefined") return [];

    return comments
      .map((item) => {
        try {
          return safeJSONParse(item);
        } catch {
          return null;
        }
      })
      .filter(Boolean)
      .reverse();
  };

  if (!isVisible) return null;

  const commentHistory = getCommentHistory();

  return (
    <>
      {/* Backdrop */}
      <div className="comment-modal__backdrop" onClick={handleBackdropClick} />

      {/* Modal Container */}
      <div className="comment-modal-container">
        {/* Floating Close Button - Center Top (Mobile Only) */}
        <button
          className="comment-modal__close-btn"
          onClick={handleClose}
          aria-label="Close"
          style={{
            transform: dragY > 0 ? `translateX(-50%) translateY(${dragY}px)` : 'translateX(-50%)',
            transition: isDragging ? 'none' : 'transform 0.3s ease-out',
            left: '50%',
          }}
        >
          <Icon icon="iconamoon:close" width="24" height="24" color="currentColor" style={{ display: 'block' }} />
        </button>

        {/* Modal */}
        <div
          className="comment-modal"
          ref={modalRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
            transition: isDragging ? 'none' : 'transform 0.3s ease-out',
          }}
        >
          {/* Drawer Bar - Mobile Only */}
          <div className="comment-modal__drawer-bar"></div>
          
          {/* Header with Close Button */}
          <div className="comment-modal__header">
            <button
              className="comment-modal__close-btn-header"
              onClick={handleClose}
              aria-label="Close"
            >
              <Icon icon="iconamoon:close" width="24" height="24" color="currentColor" style={{ display: 'block' }} />
            </button>
          </div>
          <h2 className="comment-modal__title">
            {hasHistory ? "Edit Comments/Notes" : "Add Comments/Notes"}
          </h2>

        {/* Content */}
        <div className="comment-modal__content">
          {/* Mobile Title - shown only on mobile, above textarea */}
          <h2 className="comment-modal__title-mobile">
            {hasHistory ? "Edit Comments/Notes" : "Add Comments/Notes"}
          </h2>
          <textarea
            className="comment-modal__textarea"
            placeholder="Add comments/notes..."
            value={commentText}
            onChange={handleCommentChange}
            id="edit-comment-notes"
            name="edit-comment-notes"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="sentences"
            spellCheck="false"
            inputMode="text"
            enterKeyHint="done"
            data-1p-ignore="true"
            data-lpignore="true"
            data-form-type="other"
            data-private="true"
            aria-autocomplete="none"
            aria-haspopup="false"
            role="textbox"
            readOnly={false}
          />

          {/* Comment History */}
          {hasHistory && commentHistory.length > 0 && (
            <div className="comment-modal__history">
              <button
                className="comment-modal__history-toggle"
                onClick={() => setOpenHistory(!openHistory)}
              >
                <span>Comment History</span>
                <Icon
                  icon="iconamoon:arrow-down-2"
                  width="20"
                  height="20"
                  className={`comment-modal__history-icon ${openHistory ? "comment-modal__history-icon--open" : ""}`}
                />
              </button>

              {openHistory && (
                <div className="comment-modal__history-list">
                  {commentHistory.map((item, index) => (
                    <div key={index} className="comment-modal__history-item">
                      <div className="comment-modal__history-meta">
                        <strong>{item.commentedBy}</strong> on{" "}
                        {changeDate(item.commentedAt)}
                      </div>
                      <p className="comment-modal__history-text">
                        {item.comment}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="comment-modal__footer">
          <button
            className="comment-modal__submit-btn"
            onClick={handleSaveComment}
            disabled={disableSave || loading}
          >
            {loading ? (
              <Icon icon="svg-spinners:ring-resize" width="20" height="20" />
            ) : (
              hasHistory ? "Edit" : "Add"
            )}
          </button>
        </div>
      </div>
      </div>
    </>
  );
};

export default EditCommentModal;
